import React, { useState, useEffect, useCallback } from "react";
import AllUsersTable from "./AllUsersTable";
import "./Usage.css";
import Combobox from "../../components/Combobox/Combobox";  
import { TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import Button from "../../components/Buttons/Button/Button";
import { useTranslation } from "react-i18next";
import customFetch from "../../../helpers/axios";
import useUser from "../../../hooks/useUser";
import { UsageFiltersProvider, useUsageFilters } from "../../../context";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ReferenceLine,
  Cell,
} from "recharts";

function UsageView() {
  const { t, i18n } = useTranslation('usage');
  const { dateRange, setDateRange, selectedAgent, setSelectedAgent, viewType, setViewType, secondaryView, setSecondaryView, secondaryMode, setSecondaryMode } = useUsageFilters();
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllUsers, setShowAllUsers] = useState(false);
  // Agents dropdown state
  const [agents, setAgents] = useState([{ value: 'all', label: t('page.agentsAll') }]);
  const { user } = useUser();

  // Localized tooltip renderer (memoized)
  const renderTooltip = useCallback(({ active, label, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const labelMap = {
      credits: t('page.charts.view.credits'),
      users: t('page.charts.view.users'),
      messages: t('page.charts.view.messages'),
    };
    return (
      <div
        style={{
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '8px 10px',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {new Date(label).toLocaleDateString(i18n.language)}
        </div>
        {payload.map((item) => (
          <div key={item.dataKey} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: item.color,
              }}
            />
            <span style={{ color: 'var(--muted-foreground)' }}>
              {labelMap[item.dataKey] || item.name}: {secondaryMode === 'dod' ? `${(Math.round(Number(item.value) * 10) / 10).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : (Math.round(Number(item.value) * 10) / 10).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            </span>
          </div>
        ))}
      </div>
    );
  }, [t, secondaryMode, i18n.language]);

  // Fetch data when component mounts or dateRange changes
  useEffect(() => {
    const fetchStats = async () => {
      if (!user || !user.access) return;
      
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ days: String(dateRange) });
        if (selectedAgent && selectedAgent !== 'all') {
          params.append('llm_model__model_id', String(selectedAgent));
        }
        const response = await customFetch.get(`/api/logic_layer/usage/stats/?${params.toString()}`);
        setStatsData(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [dateRange, selectedAgent, user, i18n.language, t]);

  // Fetch agents for dropdown
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await customFetch.get('/api/logic_layer/agents/dropdown_by_org/');
        const toId = (a) => a?.id ?? a?.model_id ?? a?.value ?? a?.pk ?? a?.model ?? a?.modelId;
        const toName = (a) => a?.name ?? a?.display_name ?? a?.model_name ?? String(toId(a) ?? '');
        const base = Array.isArray(res.data) ? res.data : [];
        const mapped = base
          .map(a => ({ value: String(toId(a)), label: String(toName(a)) }))
          .filter(o => o.value && o.value !== 'undefined' && o.value !== 'null');
        const options = [{ value: 'all', label: t('page.agentsAll') }, ...mapped];
        setAgents(options);
        // If current selectedAgent is not present, reset to 'all'
        const exists = options.some(o => o.value === String(selectedAgent));
        if (!exists) setSelectedAgent('all');
      } catch (e) {
        setAgents([{ value: 'all', label: t('page.agentsAll') }]);
      }
    };
    fetchAgents();
  }, [i18n.language, t, selectedAgent, setSelectedAgent]);

  // Remap the 'all' option label on language change without refetching
  useEffect(() => {
    setAgents(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return [{ value: 'all', label: t('page.agentsAll') }];
      const rest = prev.filter(o => o.value !== 'all');
      return [{ value: 'all', label: t('page.agentsAll') }, ...rest];
    });
  }, [i18n.language, t]);

  // Derived data for Secondary Chart based on selected mode
  const secondaryChartData = React.useMemo(() => {
    if (!statsData || !Array.isArray(statsData.daily_trend)) return [];
    const src = statsData.daily_trend;
    const key = secondaryView === 'credits' ? 'credits' : secondaryView;
    if (secondaryMode === 'trend') {
      return src.map(d => ({ date: d.date, [key]: d[key] }));
    }
    if (secondaryMode === 'cumulative') {
      let sum = 0;
      return src.map(d => {
        const val = Number(d[key] || 0);
        sum += isFinite(val) ? val : 0;
        return { date: d.date, [key]: sum };
      });
    }
    if (secondaryMode === 'movavg7') {
      const win = 7;
      const prefix = new Array(src.length).fill(0);
      for (let i = 0; i < src.length; i++) {
        const prev = i > 0 ? prefix[i-1] : 0;
        const val = Number(src[i][key] || 0);
        prefix[i] = prev + (isFinite(val) ? val : 0);
      }
      return src.map((d, i) => {
        const start = Math.max(0, i - win + 1);
        const total = prefix[i] - (start > 0 ? prefix[start-1] : 0);
        const count = i - start + 1;
        const avg = count > 0 ? total / count : 0;
        return { date: d.date, [key]: avg };
      });
    }
    if (secondaryMode === 'dod') {
      let prev = null;
      return src.map(d => {
        const cur = Number(d[key] || 0);
        let pct = 0;
        if (prev === null || !isFinite(prev) || prev === 0) {
          pct = 0;
        } else {
          pct = ((cur - prev) / prev) * 100;
        }
        prev = cur;
        return { date: d.date, [key]: pct };
      });
    }
    return src.map(d => ({ date: d.date, [key]: d[key] }));
  }, [statsData, secondaryView, secondaryMode]);

  // Average line for secondary chart (0 for DoD mode)
  const secondaryAvg = React.useMemo(() => {
    if (!Array.isArray(secondaryChartData) || secondaryChartData.length === 0) return 0;
    if (secondaryMode === 'dod') return 0;
    const key = secondaryView === 'credits' ? 'credits' : secondaryView;
    let sum = 0;
    let count = 0;
    for (const d of secondaryChartData) {
      const v = Number(d[key]);
      if (Number.isFinite(v)) { sum += v; count++; }
    }
    return count ? sum / count : 0;
  }, [secondaryChartData, secondaryMode, secondaryView]);

  // Bar colors for secondary chart (DoD uses per-bar coloring with gradients)
  const dodFillId = (v) => (v > 0 ? 'url(#grad-dod-pos)' : v < 0 ? 'url(#grad-dod-neg)' : 'url(#grad-dod-zero)');
  const secondaryBarColors = React.useMemo(() => {
    if (secondaryMode !== 'dod') return [];
    const key = secondaryView;
    return (secondaryChartData || []).map(d => dodFillId(Number(d[key])));
  }, [secondaryChartData, secondaryMode, secondaryView]);

  // Helper function to format trend percentage
  const formatTrendPercentage = (value) => {
    if (value === 0) return `0% ${t('page.trend.vsPrevious')}`;
    const sign = value > 0 ? "+" : "";
    return `${sign}${Math.round(value)}% ${t('page.trend.vsPrevious')}`;
  };

  // Helper function to get trend icon and color
  const getTrendIcon = (value) => {
    if (value > 0) {
      return <TrendingUp size={12} color="#16a34a" className="usage-stat-icon" />;
    } else if (value < 0) {
      return <TrendingDown size={12} color="#dc2626" className="usage-stat-icon" />;
    } else {
      return <TrendingUp size={12} color="#6b7280" className="usage-stat-icon" />;
    }
  };

  // Card colors now handled by CSS theme classes usage-stat--theme-0..7

  // Helper function to get user avatar colors
  // Avatar colors are handled via CSS classes: usage-avatar--0..7

  // Helper function to get trend class
  const getTrendClass = (value) => {
    return value >= 0 ? "usage-stat-trend-up" : "usage-stat-trend-down";
  };


  // Navegación interna: mostrar tabla de todos los usuarios
  if (showAllUsers) {
    return <AllUsersTable onBack={() => setShowAllUsers(false)} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="usage-root">
        <div className="usage-header">
          <div>
            <h1 className="usage-title">{t('page.title')}</h1>
            <p className="usage-subtitle">{t('page.subtitle')}</p>
          </div>
        </div>
        <div className="usage-center">
          <p>{t('page.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="usage-root">
        <div className="usage-header">
          <div>
            <h1 className="usage-title">{t('page.title')}</h1>
            <p className="usage-subtitle">{t('page.subtitle')}</p>
          </div>
        </div>
        <div className="usage-center usage-error">
          <p>{t('page.errorPrefix')} {error.response?.data?.detail || error.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="usage-retry-btn"
          >
            {t('page.retry')}
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!statsData) {
    return (
      <div className="usage-root">
        <div className="usage-header">
          <div>
            <h1 className="usage-title">{t('page.title')}</h1>
            <p className="usage-subtitle">{t('page.subtitle')}</p>
          </div>
        </div>
        <div className="usage-center">
          <p>{t('page.noData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-root">
      {/* Page Header */}
      <div className="usage-header">
        <div>
          <h1 className="usage-title">{t('page.title')}</h1>
          <p className="usage-subtitle">{t('page.subtitle')}</p>
        </div>
        <div className="usage-filters">
          <Combobox
            id="usage-date-range"
            value={dateRange}
            options={[
              { value: "7", label: t('page.dateRange.last7') },
              { value: "30", label: t('page.dateRange.last30') },
              { value: "90", label: t('page.dateRange.last90') }
            ]}
            onChange={setDateRange}
            className="usage-date-range"
          />

          <Combobox
            id="usage-agent-filter"
            value={selectedAgent}
            options={agents}
            onChange={setSelectedAgent}
            className="usage-date-range"
            label={t('page.agentsLabel')}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="usage-stats">
        {[ 
          { label: t('page.stats.totalCredits'), value: Math.round(statsData.total_credits).toLocaleString(), change: statsData.total_credits_change },
          { label: t('page.stats.activeUsers'), value: statsData.active_users, change: statsData.active_users_change },
          { label: t('page.stats.averagePerUser'), value: Math.round(statsData.average_credits_per_user), change: statsData.average_credits_per_user_change },
          { label: t('page.stats.totalMessages'), value: statsData.total_messages.toLocaleString(), change: statsData.total_messages_change }
        ].map((stat, index) => {
          return (
            <div
              key={stat.label}
              className={`usage-stat-card usage-stat--theme-${index % 8}`}
            >
              <div className="usage-stat-label">{stat.label}</div>
              <div className="usage-stat-value">{stat.value}</div>
              <div className={`usage-stat-trend ${getTrendClass(stat.change)}`}>
                {getTrendIcon(stat.change)}
                {formatTrendPercentage(stat.change)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Stats Cards */}
      <div className="usage-stats">
        {[ 
          { label: t('page.stats.totalIterations'), value: statsData.total_iterations.toLocaleString() },
          { label: t('page.stats.toolCalls'), value: statsData.total_tool_calls.toLocaleString() },
          { label: t('page.stats.totalPrompts'), value: statsData.total_prompts.toLocaleString() },
          { label: t('page.stats.avgCreditsPerIteration'), value: Math.round(statsData.avg_credits_per_iteration) }
        ].map((stat, index) => {
          return (
            <div
              key={stat.label}
              className={`usage-stat-card usage-stat--theme-${(index + 4) % 8}`}
            >
              <div className="usage-stat-label">{stat.label}</div>
              <div className="usage-stat-value">{stat.value}</div>
            </div>
          );
        })}
      </div>

      {/* Usage Charts */}
      <div className="usage-charts">
        {/* LEFT: stacked charts */}
        <div className="usage-left-col">
          {/* Daily Usage Trend */}
          <div className="usage-chart-card">
            <div className="usage-chart-title">{t('page.charts.dailyUsageTrend')}</div>
              <Combobox
                className="usage-date-range usage-combobox-small"
                value={viewType}
                onChange={setViewType}
                options={[
                  { value: "credits", label: t('page.charts.view.credits') },
                  { value: "users", label: t('page.charts.view.users') },
                  { value: "messages", label: t('page.charts.view.messages') },
                ]}
              />
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={statsData.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString(i18n.language, {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                  <Tooltip content={renderTooltip} />
                  <Area
                    type="monotone"
                    dataKey={viewType === 'credits' ? 'credits' : viewType}
                    stroke="var(--primary)"
                    fill="hsl(262, 83%, 95%)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
          </div>

          {/* Secondary Chart Card */}
          <div className="usage-chart-card">
            <div className="usage-chart-title">{t('page.charts.dailyBreakdown')}</div>
            <div className="usage-chart-controls">
              <div className="usage-chart-controls-flex">
                <Combobox
                  className="usage-date-range"
                  value={secondaryView}
                  onChange={setSecondaryView}
                  options={[
                    { value: "credits", label: t('page.charts.view.credits') },
                    { value: "users", label: t('page.charts.view.users') },
                    { value: "messages", label: t('page.charts.view.messages') },
                  ]}
                />
                <Combobox
                  className="usage-date-range"
                  value={secondaryMode}
                  onChange={setSecondaryMode}
                  options={[
                    { value: "trend", label: t('page.charts.mode.trend') },
                    { value: "movavg7", label: t('page.charts.mode.movavg7') },
                    { value: "cumulative", label: t('page.charts.mode.cumulative') },
                    { value: "dod", label: t('page.charts.mode.dod') },
                  ]}
                />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={secondaryChartData}>
                  <defs>
                    <linearGradient id="grad-users" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id="grad-messages" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a5b4fc" />
                      <stop offset="100%" stopColor="#6366F1" />
                    </linearGradient>
                      <linearGradient id="grad-credits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fcd34d" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                    {/* DoD gradients per sign */}
                    <linearGradient id="grad-dod-pos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#86efac" />
                      <stop offset="100%" stopColor="#16a34a" />
                    </linearGradient>
                    <linearGradient id="grad-dod-neg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fca5a5" />
                      <stop offset="100%" stopColor="#dc2626" />
                    </linearGradient>
                    <linearGradient id="grad-dod-zero" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#e5e7eb" />
                      <stop offset="100%" stopColor="#9ca3af" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString(i18n.language, {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                  <Tooltip content={renderTooltip} />
                  <ReferenceLine y={secondaryMode === 'dod' ? 0 : secondaryAvg} stroke="var(--muted-foreground)" strokeDasharray="3 3" />
                  <Bar
                    dataKey={secondaryView === 'credits' ? 'credits' : secondaryView}
                    fill={secondaryMode === 'dod' ? '#9ca3af' : `url(#grad-${secondaryView})`}
                    radius={[6, 6, 0, 0]}
                  >
                    {secondaryMode === 'dod' && (secondaryChartData || []).map((d, idx) => (
                      <Cell key={`cell-${idx}`} fill={secondaryBarColors[idx]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* RIGHT: Top Users */}
        <div className="usage-table-section usage-top-users-card">
          <div className="usage-top-users-header">
            <div className="usage-table-title">🏆 {t('page.topUsers.title')}</div>
            <Button
              variant="primary"
              size="md"
              onClick={() => setShowAllUsers(true)}
              rightIcon={<ChevronRight size={16} />}
            >
              {t('page.seeMore')}
            </Button>
          </div>
          <div className="usage-top-users-list">
            {statsData.top_users.slice(0, 5).map((user, index) => (
              <div
                key={user.email}
                className="usage-top-user-item"
              >
                <div
                  className={`usage-top-user-avatar usage-avatar--${index % 8}`}
                >
                  {index + 1}
                </div>
                <div className="usage-top-user-flex">
                  <div className="usage-top-user-row">
                    <div>
                      <div className="usage-table-user">{user.name}</div>
                      <div className="usage-table-dept">{user.email}</div>
                    </div>
                    <div className="usage-top-user-right">
                      <div className="usage-table-credits">
                        {user.total_credits.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                      </div>
                      <div className="usage-table-messages">
                        {user.total_messages} {t('page.topUsers.messages')}
                      </div>
                      <div className="usage-table-messages usage-top-user-avg">
                        {t('page.topUsers.avg')}: {user.avg_daily_credits.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {t('page.topUsers.creditsPerDay')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Usage() {
  return (
    <UsageFiltersProvider>
      <UsageView />
    </UsageFiltersProvider>
  );
}
